import { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { Lock, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  if (user && user.role === "buyer") return <Navigate to={location.state?.from || "/favorites"} replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (mode === "signup") {
        await register({ email: form.email, password: form.password, name: form.name, company: form.company });
        navigate("/favorites");
      } else {
        const u = await login(form.email, form.password);
        navigate(u.role === "admin" ? "/admin" : (location.state?.from || "/"));
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 grid-noise">
      <div className="w-full max-w-md bg-paper border-2 border-ink shadow-hardlg p-8">
        <div className="bg-ink text-paper h-12 w-12 flex items-center justify-center mb-6">
          {mode === "signup" ? <UserPlus className="h-6 w-6" /> : <Lock className="h-6 w-6" />}
        </div>

        <div className="flex border-2 border-ink mb-6">
          <button data-testid="tab-signin" onClick={() => { setMode("signin"); setError(""); }} className={`flex-1 py-2 font-mono text-xs uppercase tracking-widest ${mode === "signin" ? "bg-ink text-paper" : "bg-paper"}`}>Sign In</button>
          <button data-testid="tab-signup" onClick={() => { setMode("signup"); setError(""); }} className={`flex-1 py-2 font-mono text-xs uppercase tracking-widest ${mode === "signup" ? "bg-ink text-paper" : "bg-paper"}`}>Create Account</button>
        </div>

        <h1 className="font-heading font-black text-3xl tracking-tighter">{mode === "signup" ? "JOIN STR SURPLUS" : "WELCOME BACK"}</h1>
        <p className="font-mono text-xs text-subtle mt-2">{mode === "signup" ? "Create a buyer account so your favorites and alerts follow you everywhere." : "Sign in to sync your saved items and alerts. Owners sign in here too."}</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {mode === "signup" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" testid="reg-name" value={form.name} onChange={set("name")} />
              <Field label="Company" testid="reg-company" value={form.company} onChange={set("company")} />
            </div>
          )}
          <Field label="Email" testid="login-email" type="email" required value={form.email} onChange={set("email")} />
          <Field label="Password" testid="login-password" type="password" required value={form.password} onChange={set("password")} hint={mode === "signup" ? "At least 8 characters" : ""} />
          {error && <div data-testid="login-error" className="bg-danger text-white font-mono text-xs p-3">{error}</div>}
          <button data-testid="login-submit" disabled={busy} className="w-full bg-ink text-paper h-12 font-mono text-sm uppercase tracking-[0.15em] shadow-hard hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-transform disabled:opacity-50">
            {busy ? "Please wait…" : (mode === "signup" ? "Create Account" : "Sign In")}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, required, hint }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-wider text-subtle">{label}</span>
      <input data-testid={testid} type={type} required={required} value={value} onChange={onChange} className="mt-1 w-full border border-ink h-11 px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
      {hint && <span className="font-mono text-[10px] text-subtle">{hint}</span>}
    </label>
  );
}
